import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Home, Users as UsersIcon, History, Calculator } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useConfirm } from "../components/ConfirmProvider";
import { fmtRoDate } from "../lib/dateFmt";
import ApartmentKeysPane from "../components/rentals/ApartmentKeysPane";

const EMPTY = {
  titlu: "", adresa: "", nr_camere: "", de_la_cine: "", din_data: "",
  capacitate_persoane: "", pret_lunar: "", note: "",
};

export default function Chirii() {
  const confirm = useConfirm();
  const { canDo } = useAuth();
  const [rows, setRows] = useState([]);
  const [persons, setPersons] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    const [r, p] = await Promise.all([api.get("/rentals"), api.get("/persons")]);
    setRows(r.data);
    setPersons(p.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    try {
      const payload = {
        ...form,
        nr_camere: parseInt(form.nr_camere || 0) || 0,
        capacitate_persoane: parseInt(form.capacitate_persoane || 0) || 0,
        pret_lunar: parseFloat(form.pret_lunar || 0) || 0,
      };
      if (editing) {
        await api.put(`/rentals/${editing.id}`, payload);
        toast.success("Chirie actualizată");
      } else {
        await api.post("/rentals", payload);
        toast.success("Chirie adăugată");
      }
      setOpen(false);
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const edit = (r) => {
    setEditing(r);
    setForm({
      titlu: r.titlu || "",
      adresa: r.adresa || "", nr_camere: r.nr_camere || "", de_la_cine: r.de_la_cine || "",
      din_data: r.din_data || "", capacitate_persoane: r.capacitate_persoane || "",
      pret_lunar: r.pret_lunar || "", note: r.note || "",
    });
    setOpen(true);
  };

  const remove = async (r) => {
    if (!(await confirm({ body: `Șterge chiria ${r.titlu || r.adresa}?`, danger: true }))) return;
    await api.delete(`/rentals/${r.id}`);
    toast.success("Șters");
    load();
  };

  const setF = (k, v) => setForm((x) => ({ ...x, [k]: v }));

  const personsForRental = (rid) => persons.filter((p) => p.rental_id === rid && p.active !== false);

  return (
    <div data-testid="page-chirii">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold">Chirii</h1>
          <p className="text-sm text-zinc-500 mt-1">Cazări, proprietari, capacități și costuri lunare</p>
        </div>
        {canDo("CHIRII", "add") && (
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(EMPTY); setEditing(null); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-rental-btn" className="bg-[var(--primary)]"><Plus size={16}/> Chirie nouă</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle>{editing ? "Editează chirie" : "Chirie nouă"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2"><Label>Titlu</Label><Input data-testid="rental-titlu" value={form.titlu} onChange={(e) => setF("titlu", e.target.value)} placeholder="ex: Apartament Centru, Cameră Muncitori..." /></div>
                <div className="md:col-span-2"><Label>Adresă</Label><Input data-testid="rental-adresa" value={form.adresa} onChange={(e) => setF("adresa", e.target.value)} /></div>
                <div><Label>Nr. camere</Label><Input type="number" min={0} value={form.nr_camere} onChange={(e) => setF("nr_camere", e.target.value)} /></div>
                <div><Label>Capacitate persoane</Label><Input type="number" min={0} value={form.capacitate_persoane} onChange={(e) => setF("capacitate_persoane", e.target.value)} /></div>
                <div className="md:col-span-2"><Label>De la (proprietar / agenție / de unde)</Label><Input value={form.de_la_cine} onChange={(e) => setF("de_la_cine", e.target.value)} /></div>
                <div><Label>Din data</Label><Input type="date" value={form.din_data} onChange={(e) => setF("din_data", e.target.value)} /></div>
                <div><Label>Preț lunar</Label><Input type="number" step="0.01" value={form.pret_lunar} onChange={(e) => setF("pret_lunar", e.target.value)} /></div>
                <div className="md:col-span-2"><Label>Notă</Label><Textarea rows={2} value={form.note} onChange={(e) => setF("note", e.target.value)} /></div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setOpen(false); setEditing(null); setForm(EMPTY); }}>Anulează</Button>
                <Button data-testid="submit-rental" onClick={submit} className="bg-[var(--primary)]">Salvează</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((r) => {
          const occupants = personsForRental(r.id);
          const cap = r.capacitate_persoane || 0;
          const full = cap > 0 && occupants.length >= cap;
          return (
            <div key={r.id} className="card-flat p-5">
              <div className="flex items-start justify-between">
                <div className="w-12 h-12 rounded-md bg-[var(--primary-subtle)] text-[var(--primary)] grid place-items-center">
                  <Home size={22} />
                </div>
                <div className="text-right">
                  <div className="small-label">Preț/lună</div>
                  <div className="font-display text-lg font-extrabold">{(r.pret_lunar || 0).toFixed(2)} <span className="text-xs font-normal text-zinc-500">CHF</span></div>
                </div>
              </div>
              <div className="font-display text-lg font-bold mt-4">{r.titlu || r.adresa}</div>
              {r.titlu && <div className="text-xs text-zinc-500 mt-0.5">{r.adresa}</div>}
              <div className="text-xs text-zinc-500 mt-1">{r.nr_camere} cam · de la {r.de_la_cine || "—"} · din {r.din_data || "—"}</div>

              <div className="mt-4 flex items-center gap-2 text-sm">
                <UsersIcon size={14} className="text-zinc-400" />
                <span className={full ? "text-[var(--red-bila)] font-semibold" : "text-zinc-700"}>
                  {occupants.length} / {cap || "∞"} persoane cazate
                </span>
              </div>
              {occupants.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {occupants.map((p) => (
                    <span key={p.id} className="text-[11px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-700">
                      {p.nume} {p.prenume}
                    </span>
                  ))}
                </div>
              )}
              {r.note && <div className="text-sm text-zinc-600 mt-3">{r.note}</div>}

              <div className="mt-4 flex gap-2 justify-end flex-wrap">
                <button onClick={() => setDetail(r)} data-testid={`rental-detail-${r.id}`} className="text-[var(--primary)] text-sm hover:underline inline-flex items-center gap-1">
                  <History size={14}/> Istoric & Calcul
                </button>
                {canDo("CHIRII", "edit") && <button onClick={() => edit(r)} className="text-zinc-600 text-sm hover:underline">Editează</button>}
                {canDo("CHIRII", "delete") && <button onClick={() => remove(r)} className="text-red-600 p-1 hover:bg-red-50 rounded"><Trash2 size={14}/></button>}
              </div>
            </div>
          );
        })}
        {rows.length === 0 && <div className="text-sm text-zinc-500 col-span-full">Nicio chirie înregistrată.</div>}
      </div>

      {detail && <RentalDetailDialog rental={detail} persons={persons} onClose={() => setDetail(null)} canEdit={canDo("CHIRII", "edit")} />}
    </div>
  );
}

function RentalDetailDialog({ rental, persons, onClose, canEdit }) {
  const confirm = useConfirm();
  const [occ, setOcc] = useState([]);
  const [addForm, setAddForm] = useState({ person_id: "", start_date: new Date().toISOString().slice(0, 10), end_date: "", note: "" });
  const today = new Date();
  const [month, setMonth] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`);
  const [alloc, setAlloc] = useState(null);

  const load = async () => {
    const [o, a] = await Promise.all([
      api.get(`/rentals/${rental.id}/occupancies`),
      api.get(`/rentals/${rental.id}/allocation?month=${month}`),
    ]);
    setOcc(o.data);
    setAlloc(a.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rental.id, month]);

  const addOcc = async () => {
    if (!addForm.person_id || !addForm.start_date) { toast.error("Selectează persoană și data început"); return; }
    try {
      await api.post(`/rentals/${rental.id}/occupancies`, {
        person_id: addForm.person_id,
        start_date: addForm.start_date,
        end_date: addForm.end_date || null,
        note: addForm.note || null,
      });
      setAddForm({ person_id: "", start_date: new Date().toISOString().slice(0, 10), end_date: "", note: "" });
      toast.success("Intrare adăugată");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare"); }
  };

  const updateOcc = async (o, patch) => {
    try {
      await api.put(`/rentals/${rental.id}/occupancies/${o.id}`, {
        person_id: o.person_id,
        start_date: o.start_date,
        end_date: o.end_date,
        note: o.note,
        ...patch,
      });
      load();
    } catch (e) { toast.error("Eroare"); }
  };

  const delOcc = async (o) => {
    if (!(await confirm({ body: "Ștergi această perioadă?", danger: true }))) return;
    await api.delete(`/rentals/${rental.id}/occupancies/${o.id}`);
    load();
  };

  const closeOcc = async (o) => {
    const todayStr = new Date().toISOString().slice(0, 10);
    await updateOcc(o, { end_date: todayStr });
    toast.success("Perioadă închisă azi");
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{rental.titlu || rental.adresa}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="card-flat p-4 bg-zinc-50">
            <div className="text-xs text-zinc-500">{rental.adresa}</div>
            <div className="flex flex-wrap gap-4 mt-2 text-sm">
              <div><span className="text-zinc-500">Preț lunar:</span> <strong>{rental.pret_lunar || 0} CHF</strong></div>
              <div><span className="text-zinc-500">Capacitate:</span> <strong>{rental.capacitate_persoane || "—"}</strong></div>
              <div><span className="text-zinc-500">Camere:</span> <strong>{rental.nr_camere || 0}</strong></div>
            </div>
          </div>

          {/* Chei apartament */}
          <ApartmentKeysPane rentalId={rental.id} canEdit={canEdit} />

          {/* Istoric cazare */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <History size={18} className="text-[var(--primary)]" />
              <h3 className="font-display text-lg font-bold">Istoric cazare</h3>
            </div>

            {canEdit && (
              <div className="card-flat p-4 mb-3 grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
                <div className="md:col-span-2">
                  <Label className="small-label">Persoană</Label>
                  <Select value={addForm.person_id || ""} onValueChange={(v) => setAddForm((x) => ({ ...x, person_id: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Selectează" /></SelectTrigger>
                    <SelectContent>
                      {persons.map((p) => <SelectItem key={p.id} value={p.id}>{p.nume} {p.prenume}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="small-label">De la</Label>
                  <Input type="date" value={addForm.start_date} onChange={(e) => setAddForm((x) => ({ ...x, start_date: e.target.value }))} className="mt-1" />
                </div>
                <div>
                  <Label className="small-label">Până la (opțional)</Label>
                  <Input type="date" value={addForm.end_date} onChange={(e) => setAddForm((x) => ({ ...x, end_date: e.target.value }))} className="mt-1" />
                </div>
                <Button onClick={addOcc} className="bg-[var(--primary)]" data-testid="add-occupancy">Adaugă</Button>
              </div>
            )}

            <div className="card-flat overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50">
                  <tr className="text-left">
                    <th className="p-3">Persoană</th>
                    <th className="p-3">De la</th>
                    <th className="p-3">Până la</th>
                    <th className="p-3">Status</th>
                    {canEdit && <th className="p-3"></th>}
                  </tr>
                </thead>
                <tbody>
                  {occ.map((o) => {
                    const active = !o.end_date;
                    return (
                      <tr key={o.id} className="border-t border-[var(--border)]">
                        <td className="p-3 font-medium">{o.person_name}</td>
                        <td className="p-3">{fmtRoDate(o.start_date)}</td>
                        <td className="p-3">{o.end_date ? fmtRoDate(o.end_date) : <span className="text-zinc-400 italic">în curs</span>}</td>
                        <td className="p-3">
                          {active ? (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--green-bg)] text-[var(--green-valid)] font-semibold">ACTIV</span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600 font-semibold">ÎNCHEIAT</span>
                          )}
                        </td>
                        {canEdit && (
                          <td className="p-3 text-right">
                            {active && <button onClick={() => closeOcc(o)} className="text-xs text-[var(--primary)] hover:underline mr-2">Închide azi</button>}
                            <button onClick={() => delOcc(o)} className="text-red-600 p-1 hover:bg-red-50 rounded"><Trash2 size={14}/></button>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                  {occ.length === 0 && <tr><td colSpan={canEdit ? 5 : 4} className="p-6 text-center text-zinc-500">Fără istoric de cazare.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          {/* Calcul chirie pe lună */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Calculator size={18} className="text-[var(--primary)]" />
              <h3 className="font-display text-lg font-bold">Calcul chirie pe lună</h3>
            </div>
            <div className="card-flat p-4">
              <div className="flex items-end gap-3 mb-4 flex-wrap">
                <div>
                  <Label className="small-label">Luna</Label>
                  <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="mt-1 w-48" data-testid="allocation-month" />
                </div>
                {alloc && (
                  <div className="flex flex-wrap gap-3 ml-auto">
                    <MiniStat label="Zile în lună" v={alloc.days_in_month} />
                    <MiniStat label="Chirie/zi" v={`${alloc.daily_rent} CHF`} />
                    <MiniStat label="Zile neocupate" v={alloc.vacant_days} color={alloc.vacant_days > 0 ? "#FF8A00" : undefined} />
                  </div>
                )}
              </div>

              {alloc && (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-zinc-50">
                      <tr className="text-left">
                        <th className="p-3">Persoană</th>
                        <th className="p-3 text-right">Zile locuite</th>
                        <th className="p-3 text-right">De oprit (CHF)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {alloc.allocations.map((a) => (
                        <tr key={a.person_id} className="border-t border-[var(--border)]">
                          <td className="p-3 font-medium">{a.person_name}</td>
                          <td className="p-3 text-right">{a.days}</td>
                          <td className="p-3 text-right font-display font-extrabold text-[var(--primary)]">{a.total.toFixed(2)}</td>
                        </tr>
                      ))}
                      {alloc.vacant_days > 0 && (
                        <tr className="border-t border-[var(--border)] bg-[var(--orange-bila-bg)]">
                          <td className="p-3 italic text-[var(--orange-bila)]">Neocupat ({alloc.vacant_days} zile)</td>
                          <td className="p-3 text-right text-[var(--orange-bila)]">{alloc.vacant_days}</td>
                          <td className="p-3 text-right font-display font-extrabold text-[var(--orange-bila)]">{alloc.vacant_amount.toFixed(2)}</td>
                        </tr>
                      )}
                      {alloc.allocations.length === 0 && alloc.vacant_days === 0 && (
                        <tr><td colSpan={3} className="p-6 text-center text-zinc-500">Fără date pentru această lună.</td></tr>
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-[var(--border)] bg-zinc-50">
                        <td className="p-3 font-display font-bold">TOTAL</td>
                        <td className="p-3"></td>
                        <td className="p-3 text-right font-display font-extrabold text-lg">{alloc.monthly_rent.toFixed(2)} CHF</td>
                      </tr>
                    </tfoot>
                  </table>
                  <div className="text-xs text-zinc-500 mt-3">
                    Fiecare zi este împărțită egal între ocupanții activi în ziua respectivă. Chiria zilnică = chirie lunară ÷ zile în lună.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Închide</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MiniStat({ label, v, color }) {
  return (
    <div className="text-right">
      <div className="small-label">{label}</div>
      <div className="font-display text-lg font-extrabold" style={color ? { color } : {}}>{v}</div>
    </div>
  );
}
